import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as api from "../../../utils/api/deliveryDocumentation";
import {
  EVIDENCE_TYPES,
  OUTCOME_LABELS,
} from "../../../utils/cashier/deliveryDocumentation";
import HierarchyLevel from "./HierarchyLevel";
import { AdminPagination, AdminStatus, RetryButton } from "./AdminUi";
import { useAppLabel } from "../../../utils/useAppLabel";

const levels = [
  "restaurants",
  "branches",
  "applications",
  "months",
  "days",
  "orders",
];
const titles = {
  restaurants: "كل المطاعم",
  branches: "الفروع",
  applications: "التطبيقات",
  months: "الأشهر",
  days: "الأيام",
  orders: "الطلبات",
};
const ROOT_STACK = Object.freeze([
  { level: "restaurants", label: "كل المطاعم" },
]);

function OrderList({ filters, crumb, onOpenOrder }) {
  const appLabel = useAppLabel();
  const [state, setState] = useState({
    loading: true,
    error: "",
    items: [],
    pagination: { page: 1, totalPages: 1 },
  });
  const load = useCallback(
    async (page = 1) => {
      setState((value) => ({ ...value, loading: true, error: "" }));
      try {
        const data = await api.getAdminDocumentationOrders({
          ...filters,
          storeId: crumb.storeId,
          branchId: crumb.branchId,
          applicationId: crumb.applicationId,
          day: crumb.day,
          page,
          limit: 20,
        });
        setState({
          loading: false,
          error: "",
          items: data.items || [],
          pagination: data.pagination || {},
        });
      } catch (error) {
        setState((value) => ({
          ...value,
          loading: false,
          error: error.message,
        }));
      }
    },
    [filters, crumb],
  );
  useEffect(() => {
    load(1);
  }, [load]);
  if (state.loading)
    return <div className="admin-empty">جاري تحميل الطلبات…</div>;
  if (state.error)
    return (
      <div className="admin-empty admin-error">
        {state.error}{" "}
        <RetryButton onClick={() => load(state.pagination.page || 1)} />
      </div>
    );
  if (!state.items.length)
    return (
      <div className="admin-empty">
        لا توجد طلبات في هذا اليوم حسب الفلاتر الحالية.
      </div>
    );
  return (
    <>
      <div className="admin-orders">
        {state.items.map((order) => (
          <article className="admin-order" key={order._id}>
            <button className="admin-order-summary" type="button" onClick={() => onOpenOrder(order._id)}>
              <div>
                <div className="admin-order-title">#{order.last4}</div>
                <div className="admin-order-meta">
                  {appLabel(order.applicationSnapshot?.app, order.applicationSnapshot?.name)} ·{" "}
                  {order.branchSnapshot?.name} · {order.businessDate}
                </div>
              </div>
              <div className="admin-order-side">
                <AdminStatus status={order.documentationStatus === "complete" ? "done" : "pending"}>
                  {order.documentationStatus === "complete"
                    ? "مكتمل"
                    : "قيد التوثيق"}
                </AdminStatus>
                {order.outcome !== "normal" && (
                  <AdminStatus status={order.outcome}>
                    {OUTCOME_LABELS[order.outcome]}
                  </AdminStatus>
                )}
                {order.courierNotPickedUp && (
                  <AdminStatus status="pending">لم يستلم</AdminStatus>
                )}
                {order.matchStatus && (
                  <AdminStatus>{order.matchStatus}</AdminStatus>
                )}
              </div>
            </button>
            <div className="admin-order-body">
              <div className="admin-photo-grid">
                {EVIDENCE_TYPES.map((type) => {
                  const file = order.evidence?.[type.key];
                  return file?.url ? (
                    <div className="admin-photo" key={type.key}>
                      <img src={file.url} alt={type.label} />
                      <div className="cap">{type.label}</div>
                    </div>
                  ) : (
                    <div className="admin-photo admin-missing" key={type.key}>
                      {type.label}
                      <br />
                      لا توجد صورة
                    </div>
                  );
                })}
              </div>
              {order.notes && <div className="note">{order.notes}</div>}
            </div>
          </article>
        ))}
      </div>
      <AdminPagination
        page={state.pagination.page}
        totalPages={state.pagination.totalPages}
        hasPreviousPage={state.pagination.hasPreviousPage}
        hasNextPage={state.pagination.hasNextPage}
        onPrevious={() => load(state.pagination.page - 1)}
        onNext={() => load(state.pagination.page + 1)}
      />
    </>
  );
}

export default function DocumentationHierarchy({ filters, onOpenOrder }) {
  const appLabel = useAppLabel();
  const [stack, setStack] = useState(ROOT_STACK);
  const [cache, setCache] = useState({});
  const cacheRef = useRef({});
  const current = stack[stack.length - 1];
  const cacheKey = useMemo(
    () => JSON.stringify({ filters, current }),
    [filters, current],
  );
  const crumb = useMemo(
    () => Object.assign({}, ...stack.map((x) => x.filter || {})),
    [stack],
  );
  const load = useCallback(async () => {
    if (current.level === "orders") return;
    const existing = cacheRef.current[cacheKey];
    if (existing?.loading || existing?.loading === false) return;
    cacheRef.current = {
      ...cacheRef.current,
      [cacheKey]: { loading: true, error: "", items: [] },
    };
    setCache(cacheRef.current);
    try {
      const requestFilters = { ...filters, ...crumb };
      let data;
      if (current.level === "restaurants")
        data = await api.getAdminHierarchyRestaurants(requestFilters);
      if (current.level === "branches")
        data = await api.getAdminHierarchyBranches(
          crumb.storeId,
          requestFilters,
        );
      if (current.level === "applications")
        data = await api.getAdminHierarchyApplications(
          crumb.branchId,
          requestFilters,
        );
      if (current.level === "months")
        data = await api.getAdminHierarchyMonths(
          crumb.applicationId,
          requestFilters,
        );
      if (current.level === "days")
        data = await api.getAdminHierarchyDays(
          crumb.applicationId,
          crumb.month,
          requestFilters,
        );
      const next = { loading: false, error: "", items: data?.items || [] };
      const previous = cacheRef.current[cacheKey];
      if (
        previous?.loading !== next.loading ||
        previous?.error !== next.error ||
        JSON.stringify(previous?.items || []) !== JSON.stringify(next.items)
      ) {
        cacheRef.current = { ...cacheRef.current, [cacheKey]: next };
        setCache(cacheRef.current);
      }
    } catch (error) {
      const next = { loading: false, error: error.message, items: [] };
      const previous = cacheRef.current[cacheKey];
      if (previous?.loading !== next.loading || previous?.error !== next.error || previous?.items?.length) {
        cacheRef.current = { ...cacheRef.current, [cacheKey]: next };
        setCache(cacheRef.current);
      }
    }
  }, [cacheKey, current.level, filters, crumb]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    setStack((value) => (value.length === 1 && value[0].level === "restaurants" ? value : ROOT_STACK));
    if (Object.keys(cacheRef.current).length) {
      cacheRef.current = {};
      setCache({});
    }
  }, [filters]);
  const enter = useCallback((item) => {
    const nextLevel = levels[levels.indexOf(current.level) + 1];
    const filterKeys = {
      restaurants: "storeId",
      branches: "branchId",
      applications: "applicationId",
      months: "month",
      days: "day",
    };
    setStack((value) => [
      ...value,
      {
        level: nextLevel,
        label:
          current.level === "applications"
            ? appLabel(item.app, item.name) || item.id
            : item.app || item.name || item.id,
        filter: { [filterKeys[current.level]]: item.id },
      },
    ]);
  }, [current.level, appLabel]);
  const currentState = cache[cacheKey] || {
    loading: true,
    error: "",
    items: [],
  };
  return (
    <div className="doc-drill-shell">
      <div className="doc-drill-top">
        <div className="doc-crumbs">
          {stack.map((item, index) => (
            <button
              className={`doc-crumb ${index === stack.length - 1 ? "current" : ""}`}
              type="button"
              key={`${item.level}-${index}`}
              onClick={() => setStack(stack.slice(0, index + 1))}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="doc-drill-title">{titles[current.level]}</div>
      </div>
      {current.level === "orders" ? (
        <OrderList filters={filters} crumb={crumb} onOpenOrder={onOpenOrder} />
      ) : (
        <HierarchyLevel
          level={current.level}
          {...currentState}
          onRetry={load}
          onSelect={enter}
        />
      )}
    </div>
  );
}
